import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  IconButton,
  Badge,
  VStack,
  Text,
  Flex,
  Button,
  useColorModeValue,
  Divider,
  HStack,
  useToast,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
} from "@chakra-ui/react";
import { BellIcon, CheckIcon, createIcon, DeleteIcon } from "@chakra-ui/icons";
import { useAppDispatch, useAppSelector } from "@/store";
import { fetchNotifications, markViewed, markUnviewed, markAllViewed, deleteAllArchive } from "@/store/slices/notifications";
import { Notification } from "@/model/notifications";
import { useNavigate } from "react-router-dom";
import { ArrowRightIcon } from "lucide-react";
import { getNotificationBtnLink, getNotificationTitle } from "./utils";

// Кастомная иконка двойной галочки
const DoneAllIcon = createIcon({
  displayName: "DoneAllIcon",
  viewBox: "0 0 24 24",
  path: (
    <>
      <path
        fill="currentColor"
        d="M18 7l-1.41-1.41-6.34 6.34 1.41 1.41L18 7zm4.24-1.41L11.66 16.17 7.48 12l-1.41 1.41L11.66 19l12-12-1.42-1.41zM.41 13.41L6 19l1.41-1.41L1.83 12 .41 13.41z"
      />
    </>
  ),
});

export const NotificationBell: React.FC = () => {
  const dispatch = useAppDispatch();
  const { notifications } = useAppSelector((state) => state.notifications);
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const toast = useToast();

  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("#e5e7eb", "#e5e7eb");
  const textColor = useColorModeValue("blue.600", "white");

  const unreadCount = notifications.filter((n) => !n.viewed_at).length;
  const newNotifications = notifications.filter((n) => !n.viewed_at);
  const archivedNotifications = notifications.filter((n) => n.viewed_at);

  // Загрузка новых уведомлений при монтировании
  useEffect(() => {
    dispatch(fetchNotifications({ only_new: false, only_viewed: false }));
  }, [dispatch]);

  // Слушаем сообщения от Service Worker о новых уведомлениях
  useEffect(() => {
    const handleServiceWorkerMessage = (event: MessageEvent) => {
      if (event.data && event.data.type === "NEW_NOTIFICATION") {
        // Обновляем список уведомлений когда приходит новое
        dispatch(fetchNotifications({ only_new: false, only_viewed: false }));

      }
    };

    navigator.serviceWorker?.addEventListener("message", handleServiceWorkerMessage);

    return () => {
      navigator.serviceWorker?.removeEventListener("message", handleServiceWorkerMessage);
    };
  }, [dispatch, toast]);

  // Загрузка уведомлений в зависимости от активной вкладки
  useEffect(() => {
    if (isOpen) {
      if (activeTab === 0) {
        // Вкладка "Новые"
        dispatch(fetchNotifications({ only_new: false, only_viewed: false }));
      } else {
        // Вкладка "Архив"
        dispatch(fetchNotifications({ only_new: false, only_viewed: false }));
      }
    }
  }, [activeTab, isOpen, dispatch]);

  // Закрытие при клике вне области
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const handleMarkAsRead = async (id: number) => {
    await dispatch(markViewed(id));
    await dispatch(fetchNotifications({ only_new: false, only_viewed: false }));
  };

  const handleMarkAllAsRead = async () => {
    await dispatch(markAllViewed());
    await dispatch(fetchNotifications({ only_new: false, only_viewed: false }));
    toast({
      title: "Все уведомления отмечены как прочитанные",
      status: "success",
      duration: 2000,
      isClosable: true,
    });
  };

  const handleClearAllArchive = async () => {
    await dispatch(deleteAllArchive());
    await dispatch(fetchNotifications({ only_new: false, only_viewed: false }));
    toast({
      title: "Архив очищен",
      status: "success",
      duration: 2000,
      isClosable: true,
    });
  };

  const handleMarkAsUnread = async (id: number) => {
    await dispatch(markUnviewed(id));
    await dispatch(fetchNotifications({ only_new: false, only_viewed: false }));
    toast({
      title: "Уведомление возвращено в новые",
      status: "success",
      duration: 2000,
      isClosable: true,
    });
  };

  const navigate = useNavigate();

  const renderNotificationsList = (notificationsList: Notification[], emptyMessage: string, isArchive: boolean = false) => {
    if (notificationsList.length === 0) {
      return (
        <Flex direction="column" align="center" justify="center" p={8} color="gray.500">
          <BellIcon boxSize={12} opacity={0.3} />
          <Text mt={4} fontSize="sm">
            {emptyMessage}
          </Text>
        </Flex>
      );
    }

    return (
      <VStack spacing={0} align="stretch">
        {notificationsList.map((notification, index) => (
          <React.Fragment key={notification.id}>
            <Box p={4} cursor="pointer" _hover={{ bg: "gray.50" }} bg={notification.viewed_at ? "transparent" : "blue.50"} position="relative">
              <Flex gap={3} marginLeft={4}>
                <Box flex={1} minW={0}>
                  <Flex justify="space-between" align="start" mb={1}>
                    <Text fontSize="sm" fontWeight={notification.viewed_at ? "normal" : "semibold"} color="gray.800" noOfLines={2}>
                      {getNotificationTitle(notification.model)}
                    </Text>
                    <HStack spacing={1}>
                      {isArchive ? (
                        // Кнопки для архива
                        <>
                          <IconButton
                            aria-label="Вернуть в новые"
                            icon={<BellIcon boxSize={3} />}
                            size="xs"
                            variant="outline"
                            color="blue.500"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleMarkAsUnread(notification.id);
                            }}
                          />
                        </>
                      ) : (
                        // Кнопки для новых уведомлений
                        <>
                          <IconButton
                            aria-label="Отметить как прочитанное"
                            icon={<DoneAllIcon boxSize={4} />}
                            size="xs"
                            variant="outline"
                            color="green.500"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleMarkAsRead(notification.id);
                            }}
                          />
                        </>
                      )}
                    </HStack>
                  </Flex>
                  <Text fontSize="xs" color="gray.500" mt={2}>
                    {notification.message}
                  </Text>
                  {!notification.viewed_at && (
                    <Button
                      marginLeft="-6px"
                      marginTop={2}
                      size="xs"
                      colorScheme="blue"
                      variant="ghost"
                      rightIcon={<ArrowRightIcon size={16} />}
                      onClick={() => navigate(getNotificationBtnLink(notification.model, notification.model_id))}
                    >
                      Открыть
                    </Button>
                  )}
                </Box>
              </Flex>
              {!notification.viewed_at && (
                <Box position="absolute" left={2} top="50%" transform="translateY(-50%)" w={2} h={2} bg="blue.500" borderRadius="full" />
              )}
            </Box>
            {index < notificationsList.length - 1 && <Divider />}
          </React.Fragment>
        ))}
      </VStack>
    );
  };

  return (
    <Box position="relative">
      <IconButton
        aria-label="Уведомления"
        icon={
          <Box position="relative">
            <BellIcon boxSize={5} />
            {unreadCount > 0 && (
              <Badge
                position="absolute"
                top="-8px"
                right="-8px"
                colorScheme="red"
                borderRadius="full"
                fontSize="xs"
                minW="18px"
                h="18px"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                {unreadCount > 99 ? "99+" : unreadCount}
              </Badge>
            )}
          </Box>
        }
        variant="ghost"
        color={textColor}
        _hover={{ bg: "blue.50" }}
        onClick={() => setIsOpen(!isOpen)}
        size="sm"
      />

      {isOpen && (
        <Box
          ref={dropdownRef}
          position="absolute"
          top="40px"
          right="0"
          zIndex={9999}
          w="380px"
          maxH="max-content"
          bg={bgColor}
          border="1px solid"
          borderColor={borderColor}
          borderRadius="lg"
          boxShadow="0 10px 25px rgba(59, 130, 246, 0.15)"
          overflow="hidden"
        >
          {/* Заголовок */}
          <Flex p={4} borderBottom="1px solid" borderColor={borderColor} alignItems="center" justifyContent="space-between">
            <Text fontSize="lg" fontWeight="semibold" color="blue.700">
              Уведомления
            </Text>
          </Flex>

          {/* Табы с уведомлениями */}
          <Tabs size="sm" variant="enclosed" colorScheme="blue" index={activeTab} onChange={setActiveTab}>
            <TabList px={4} pt={2}>
              <Tab fontSize="sm" fontWeight="medium" position="relative">
                Новые
                {newNotifications.length > 0 && (
                  <Badge
                    ml={2}
                    colorScheme="red"
                    borderRadius="full"
                    fontSize="xs"
                    minW="18px"
                    h="18px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    {newNotifications.length}
                  </Badge>
                )}
              </Tab>
              <Tab fontSize="sm" fontWeight="medium">
                Архив
                {archivedNotifications.length > 0 && (
                  <Badge
                    ml={2}
                    colorScheme="gray"
                    borderRadius="full"
                    fontSize="xs"
                    minW="18px"
                    h="18px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    {archivedNotifications.length}
                  </Badge>
                )}
              </Tab>
            </TabList>

            <TabPanels>
              <TabPanel p={0}>
                {/* Кнопки управления для новых уведомлений */}
                {newNotifications.length > 0 && (
                  <Flex p={3} borderBottom="1px solid" borderColor={borderColor} justifyContent="flex-end" gap={2}>
                    <Button size="xs" variant="ghost" colorScheme="blue" onClick={handleMarkAllAsRead} leftIcon={<CheckIcon boxSize={3} />}>
                      Прочитать все
                    </Button>
                  </Flex>
                )}
                <Box maxH="50vh" overflowY="auto">
                  {renderNotificationsList(newNotifications, "Нет новых уведомлений", false)}
                </Box>
              </TabPanel>
              <TabPanel p={0}>
                {/* Кнопки управления для архива */}
                {archivedNotifications.length > 0 && (
                  <Flex p={3} borderBottom="1px solid" borderColor={borderColor} justifyContent="flex-end" gap={2}>
                    <Button size="xs" variant="ghost" colorScheme="red" onClick={handleClearAllArchive} leftIcon={<DeleteIcon boxSize={3} />}>
                      Очистить все
                    </Button>
                  </Flex>
                )}
                <Box maxH="50vh" overflowY="auto">
                  {renderNotificationsList(archivedNotifications, "Архив пуст", true)}
                </Box>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </Box>
      )}
    </Box>
  );
};
